import { ApiResponse, HttpClient } from '@store/base';
import {
  AsCareApplyComplete,
  AsCareApplySheet,
  AsCareAppliedOrderLine,
  AsCareCancelList,
  AsCareCancelReceipt,
  AsCareCancelledOrder,
  AsCareCancelledOrderLine,
  AsCareEligibleOrderLine,
  AsCareIneligibleOrderLine,
  AsCareList,
  CancelAsCareGateway,
  CancelAsCareRequest,
  CompleteAsCareApplyGateway,
  CompleteAsCareApplyRequest,
  GetAsCareApplySheetGateway,
  GetAsCareApplySheetRequest,
  GetAsCareCancelListGateway,
  GetAsCareListGateway,
  GetAsCarePayRequestGateway,
  GetAsCarePayRequestRequest,
  GetAsCarePayRequestResponse,
} from '@store/order';

import {
  AsCareBatchCard,
  AsCareItem,
  AsCareOrderGroup,
  AsCareOrderStatus,
  AsCareRefundMethod,
  AsCareTab,
} from '@/feat-components/My/AsCare/types';

/**
 * 한샘 AS케어+ Client Gateway — API 응답을 화면 타입으로 옮기는 구간.
 *
 * 화면(`feat-components/My/AsCare`)은 주문번호로 묶은 `AsCareOrderGroup[]` 을 다룬다.
 * API 는 반대로 **탭(신청가능·가입완료·신청불가)이 최상위**이고 한 주문번호의 상품이
 * 세 탭에 동시에 걸린다. 그 차이를 흡수하는 것이 이 파일의 일이다.
 *
 * 변환 원칙:
 * - 금액·보증구간·환급액은 **서버값을 그대로** 싣는다(화면에서 재계산하지 않는다).
 * - 탭 분류도 서버 그룹을 그대로 `item.tab` 에 싣는다 — 화면의 날짜 재판정과 갈리면
 *   「목록엔 있는데 신청은 반려되는」 상태가 생긴다.
 * - 신청·취소 요청의 식별자(`ordSeq`·`ewRegNo`·`ewPayGrpNo`)를 반드시 함께 싣는다.
 *   화면이 배열 인덱스만 들고 있으면 재조회로 순서가 바뀌는 순간 다른 상품을 신청한다.
 */

/**
 * 화면 상품명 = `상품명 [옵션명]`.
 *
 * 화면은 대괄호 안을 옵션으로 읽어(`splitName`) 서브라인에 따로 표기한다. API 가
 * `gdsNm`·`unitNm` 을 나눠 주므로 화면 관례에 맞춰 다시 합친다.
 */
const composeName = (gdsNm: string, unitNm?: string): string =>
  unitNm ? `${gdsNm} [${unitNm}]` : gdsNm;

/** 환급 방법 문구 → 화면 배지 구분. 문구 자체는 `refundMethodNm` 으로 따로 싣는다 */
const toRefundMethod = (refundMethodNm?: string): AsCareRefundMethod =>
  refundMethodNm?.includes('카드') ? 'card' : 'transfer';

const toEligibleItem = (line: AsCareEligibleOrderLine): AsCareItem => ({
  name: composeName(line.gdsNm, line.unitNm),
  amount: line.totPayAmt,
  qty: line.ordQty,
  deliveryDate: line.deliveryDate,
  status: 'eligible',
  tab: 'available',
  ordSeq: line.ordSeq,
  gdsNo: line.gdsNo,
  imgUrl: line.imgUrl,
});

const toAppliedItem = (
  line: AsCareAppliedOrderLine,
  ewPayGrpNo: string,
): AsCareItem => ({
  name: composeName(line.gdsNm, line.unitNm),
  amount: line.totPayAmt,
  qty: line.ordQty,
  deliveryDate: line.deliveryDate,
  status: 'applied_done',
  tab: 'done',
  /** 가입완료 카드는 신청 회차(= 보험결제그룹) 단위로 나뉜다 */
  batchId: ewPayGrpNo,
  ordSeq: line.ordSeq,
  gdsNo: line.gdsNo,
  imgUrl: line.imgUrl,
  ewRegNo: line.ewRegNo,
  premium: line.ewFeeAmt,
  refund: line.expectedEwFeeRefundAmt,
  fwStartDate: line.fwStartDate,
  fwEndDate: line.fwEndDate,
  ewStartDate: line.ewStartDate,
  ewEndDate: line.ewEndDate,
});

const toIneligibleItem = (line: AsCareIneligibleOrderLine): AsCareItem => ({
  name: composeName(line.gdsNm, line.unitNm),
  amount: line.totPayAmt,
  qty: line.ordQty,
  deliveryDate: line.deliveryDate,
  status: 'ineligible',
  tab: 'unavailable',
  ordSeq: line.ordSeq,
  gdsNo: line.gdsNo,
  imgUrl: line.imgUrl,
  ineligibleReasonText: line.ineligibleReason,
});

const toCancelledItem = (
  line: AsCareCancelledOrderLine,
  cancelBatchId: string,
  refundMethodNm: string,
  cancelReqDate: string,
): AsCareItem => ({
  name: composeName(line.gdsNm, line.unitNm),
  amount: line.totPayAmt,
  qty: line.ordQty,
  deliveryDate: line.deliveryDate,
  status: 'applied_cancelled',
  cancelState: 'cancelled',
  cancelBatchId,
  refundDate: cancelReqDate,
  refundMethod: toRefundMethod(refundMethodNm),
  /** 회차 단위 값이다 — 같은 주문의 다른 회차와 환급 방법이 다를 수 있다 */
  refundMethodNm,
  ordSeq: line.ordSeq,
  gdsNo: line.gdsNo,
  imgUrl: line.imgUrl,
  ewRegNo: line.ewRegNo,
  premium: line.ewFeeAmt,
  /** 취소내역은 접수일 기준 **확정** 환급액이다 */
  refund: line.ewFeeRefundAmt,
});

/**
 * 주문번호로 상품을 모은다.
 *
 * 세 그룹에 흩어진 같은 주문번호를 한 `AsCareOrderGroup` 으로 합친다 — 화면은 주문
 * 단위로 표를 세우고, 상품별 `status`/`tab` 으로 탭을 가른다. 첫 등장 순서를 유지해
 * BE 가 정한 정렬을 뒤집지 않는다.
 */
const mergeByOrderNo = (
  entries: { ordNo: string; saleShopNm: string; items: AsCareItem[] }[],
): AsCareOrderGroup[] => {
  const byOrderNo = new Map<string, AsCareOrderGroup>();
  /** Map 순회는 ES5 타깃에서 막혀 있어 등장 순서를 따로 들고 있는다 */
  const ordered: AsCareOrderGroup[] = [];

  entries.forEach(({ ordNo, saleShopNm, items }) => {
    const found = byOrderNo.get(ordNo);

    if (found) {
      found.items.push(...items);

      return;
    }

    const group: AsCareOrderGroup = {
      orderNo: ordNo,
      store: saleShopNm,
      /**
       * 주문 단위 상태는 **상품 상태의 폴백**일 뿐이다(모든 상품이 자기 `status` 를
       * 들고 있다). 첫 상품 기준으로 채워 두면 `itemStatusOf` 가 어긋나지 않는다.
       */
      status: items[0]?.status ?? 'eligible',
      items: [...items],
    };

    byOrderNo.set(ordNo, group);
    ordered.push(group);
  });

  return ordered;
};

/** SC-02 탭 라벨 건수 — 카드 수가 아니라 **상품 건수**다 (BE `orderLineCount`) */
export interface AsCareTabCounts {
  available: number;
  done: number;
  unavailable: number;
}

export interface ClientAsCareList {
  groups: AsCareOrderGroup[];
  counts: AsCareTabCounts;
}

export interface ClientGetAsCareListResponse
  extends ApiResponse<ClientAsCareList> {}

const EMPTY_LIST: ClientAsCareList = {
  groups: [],
  counts: { available: 0, done: 0, unavailable: 0 },
};

export const toClientAsCareList = (list: AsCareList): ClientAsCareList => ({
  groups: mergeByOrderNo([
    ...(list.eligible?.orderList ?? []).map((order) => ({
      ordNo: order.ordNo,
      saleShopNm: order.saleShopNm,
      items: (order.orderLines ?? []).map(toEligibleItem),
    })),
    ...(list.applied?.orderList ?? []).map((order) => ({
      ordNo: order.ordNo,
      saleShopNm: order.saleShopNm,
      items: (order.orderLines ?? []).map((line) =>
        toAppliedItem(line, order.ewPayGrpNo),
      ),
    })),
    ...(list.ineligible?.orderList ?? []).map((order) => ({
      ordNo: order.ordNo,
      saleShopNm: order.saleShopNm,
      items: (order.orderLines ?? []).map(toIneligibleItem),
    })),
  ]),
  counts: {
    available: list.eligible?.orderLineCount ?? 0,
    done: list.applied?.orderLineCount ?? 0,
    unavailable: list.ineligible?.orderLineCount ?? 0,
  },
});

/**
 * 보험신청/내역 조회 (SC-02).
 * 실패 시 빈 목록으로 떨어진다 — 화면은 「대상 없음」 문구를 그대로 쓴다.
 */
export class ClientGetAsCareListGateway {
  constructor(readonly httpClient: HttpClient) {}

  async exec(): Promise<ClientGetAsCareListResponse> {
    const apiResponse = await new GetAsCareListGateway(this.httpClient).exec();

    if (apiResponse.code !== 200 || !apiResponse.data) {
      return { ...apiResponse, data: EMPTY_LIST };
    }

    return { ...apiResponse, data: toClientAsCareList(apiResponse.data) };
  }
}

export interface ClientAsCareCancelList {
  /**
   * 취소 회차 카드 — BE `cancelled[]` 와 **1:1**이며 순서도 그대로다.
   *
   * 주문번호로 합치지 않는다. BE 가 이미 「한 번에 취소한 묶음」 단위로 잘라 주므로
   * (`CancelledOrder` = 취소 회차) 여기서 할 일은 옮겨 담는 것뿐이다.
   *
   * 예전에는 `mergeByOrderNo` 로 주문 단위로 접었다가 `ewPayGrpNo` 로 다시 폈는데,
   * 그 왕복이 손실을 냈다 — BE 는 **취소 회차**로 자르고 화면은 **신청 회차**로 다시
   * 붙이니, 한 주문을 나눠 취소하면 두 회차가 한 카드로 뭉쳤다 [QAISSUE-10414].
   */
  cards: AsCareBatchCard[];
  /** 취소 상품 건수 (BE `orderLineCount`) */
  count: number;
}

export interface ClientGetAsCareCancelListResponse
  extends ApiResponse<ClientAsCareCancelList> {}

/**
 * 취소 회차 하나 → 카드 하나.
 *
 * 상품 순서·금액·환급 방법·접수일 모두 그 회차의 서버값을 그대로 싣는다. 환급 방법과
 * 접수일은 **주문이 아니라 회차에 붙는 값**이라(정책서 9-5절) 주문 단위로 들고 있으면
 * 다른 회차의 값을 표기하게 된다.
 */
const toCancelCard = (
  order: AsCareCancelledOrder,
  cancelSeq: number,
): AsCareBatchCard => {
  const items = (order.orderLines ?? []).map((line) =>
    toCancelledItem(
      line,
      order.ewPayGrpNo,
      order.refundMethodNm,
      order.cancelReqDate,
    ),
  );
  const refundMethod = toRefundMethod(order.refundMethodNm);
  /** 카드가 곧 회차라 원본 주문에도 이 회차의 상품만 담는다 */
  const source: AsCareOrderGroup = {
    orderNo: order.ordNo,
    store: order.saleShopNm,
    status: 'applied_cancelled' as AsCareOrderStatus,
    items,
    refundMethod,
    refundMethodNm: order.refundMethodNm,
    refundDate: order.cancelReqDate,
  };

  return {
    orderNo: order.ordNo,
    store: order.saleShopNm,
    status: 'applied_cancelled' as AsCareOrderStatus,
    rows: items.map((item, index) => ({ item, index })),
    cancelBatchId: order.ewPayGrpNo,
    cancelSeq,
    refundMethod,
    refundDate: order.cancelReqDate,
    source,
  };
};

export const toClientAsCareCancelList = (
  list: AsCareCancelList,
): ClientAsCareCancelList => ({
  cards: (list.cancelled ?? []).map(toCancelCard),
  count: list.orderLineCount ?? 0,
});

/** 보험취소/내역 조회 (SC-08) */
export class ClientGetAsCareCancelListGateway {
  constructor(readonly httpClient: HttpClient) {}

  async exec(): Promise<ClientGetAsCareCancelListResponse> {
    const apiResponse = await new GetAsCareCancelListGateway(
      this.httpClient,
    ).exec();

    if (apiResponse.code !== 200 || !apiResponse.data) {
      return { ...apiResponse, data: { cards: [], count: 0 } };
    }

    return { ...apiResponse, data: toClientAsCareCancelList(apiResponse.data) };
  }
}

/**
 * 보험신청서 (SC-03) — 화면 모양.
 *
 * 상품 행은 SC-02 와 같은 `AsCareItem` 으로 맞춘다. 신청서에만 있는 금액 구성
 * (판매가·가격할인·상품쿠폰할인)도 같은 객체에 실어, `priceBreakdown` 을 비롯한
 * 기존 컴포넌트가 손대지 않고 서버값을 읽게 한다.
 */
export interface ClientAsCareApplySheet {
  orderNo: string;
  store: string;
  /** 신청 상품 수 (「상품 N개 총 보험료」 표기용) */
  orderLineCount: number;
  totOrdPayAmt: number;
  totEwFeeAmt: number;
  /** true 면 「(결제쿠폰 할인 미적용)」 문구를 노출한다 */
  payCouponApplied: boolean;
  items: AsCareItem[];
  /** 계약자 — 주문 데이터의 값이며 화면에서 수정할 수 없다 */
  contractor: { name: string; phone: string };
}

export interface ClientGetAsCareApplySheetResponse
  extends ApiResponse<ClientAsCareApplySheet> {}

export const toClientAsCareApplySheet = (
  sheet: AsCareApplySheet,
): ClientAsCareApplySheet => ({
  orderNo: sheet.ordNo,
  store: sheet.saleShopNm,
  orderLineCount: sheet.orderLineCount,
  totOrdPayAmt: sheet.totOrdPayAmt,
  totEwFeeAmt: sheet.totEwFeeAmt,
  payCouponApplied: sheet.payCouponAppliedYn,
  items: (sheet.orderLines ?? []).map((line) => ({
    name: composeName(line.gdsNm, line.unitNm),
    amount: line.totPayAmt,
    qty: line.ordQty,
    deliveryDate: line.deliveryDate,
    status: 'eligible' as AsCareOrderStatus,
    tab: 'available' as AsCareTab,
    ordSeq: line.ordSeq,
    gdsNo: line.gdsNo,
    premium: line.ewFeeAmt,
    fwStartDate: line.fwStartDate,
    fwEndDate: line.fwEndDate,
    ewStartDate: line.ewStartDate,
    ewEndDate: line.ewEndDate,
    normalSalePrc: line.normalSalePrc,
    dcAmt: line.dcAmt,
    cponAmt: line.cponAmt,
    ewFeeRate: line.ewFeeRate,
  })),
  contractor: {
    name: sheet.contractor?.orderNm ?? '',
    phone: sheet.contractor?.orderMobileNo ?? '',
  },
});

/** 보험신청서 조회 (SC-03) */
export class ClientGetAsCareApplySheetGateway {
  constructor(readonly httpClient: HttpClient) {}

  async exec(
    req: GetAsCareApplySheetRequest,
  ): Promise<ClientGetAsCareApplySheetResponse> {
    const apiResponse = await new GetAsCareApplySheetGateway(
      this.httpClient,
    ).exec(req);

    if (apiResponse.code !== 200 || !apiResponse.data) {
      return { ...apiResponse, data: undefined };
    }

    return { ...apiResponse, data: toClientAsCareApplySheet(apiResponse.data) };
  }
}

/**
 * 보험료 결제요청 파라미터 조회 (SC-04 Step 2).
 *
 * 변환하지 않는다 — `payParams` 는 `P_CHKFAKE` 로 봉인된 묶음이라 값을 하나라도
 * 손대면 위변조로 판정된다. 래퍼는 Base Gateway 격리 목적으로만 둔다.
 */
export class ClientGetAsCarePayRequestGateway {
  constructor(readonly httpClient: HttpClient) {}

  async exec(
    req: GetAsCarePayRequestRequest,
  ): Promise<GetAsCarePayRequestResponse> {
    return await new GetAsCarePayRequestGateway(this.httpClient).exec(req);
  }
}

/** SC-05 신청완료 화면 모양 */
export interface ClientAsCareApplyComplete {
  ewPayGrpNo: string;
  orderNo: string;
  store: string;
  totOrdPayAmt: number;
  /** 결제수단 (카드사명·할부개월 없이 수단만) */
  payMethodNm: string;
  /** 총 보험료 = 실제 결제금액 */
  totEwFeeAmt: number;
  items: AsCareItem[];
}

export interface ClientCompleteAsCareApplyResponse
  extends ApiResponse<ClientAsCareApplyComplete> {}

export const toClientAsCareApplyComplete = (
  complete: AsCareApplyComplete,
): ClientAsCareApplyComplete => ({
  ewPayGrpNo: complete.ewPayGrpNo,
  orderNo: complete.ordNo,
  store: complete.saleShopNm,
  totOrdPayAmt: complete.totOrdPayAmt,
  payMethodNm: complete.payMethodNm,
  totEwFeeAmt: complete.totEwFeeAmt,
  items: (complete.orderLines ?? []).map((line) =>
    toAppliedItem(line, complete.ewPayGrpNo),
  ),
});

/** 보험신청 확정 (SC-05) — PG 인증결과를 넘기면 서버가 승인까지 처리한다 */
export class ClientCompleteAsCareApplyGateway {
  constructor(readonly httpClient: HttpClient) {}

  async exec(
    req: CompleteAsCareApplyRequest,
  ): Promise<ClientCompleteAsCareApplyResponse> {
    const apiResponse = await new CompleteAsCareApplyGateway(
      this.httpClient,
    ).exec(req);

    if (apiResponse.code !== 200 || !apiResponse.data) {
      return { ...apiResponse, data: undefined };
    }

    return {
      ...apiResponse,
      data: toClientAsCareApplyComplete(apiResponse.data),
    };
  }
}

/** SC-07 취소 접수 완료 화면 모양 */
export interface ClientAsCareCancelReceipt {
  ewPayGrpNo: string;
  orderNo: string;
  store: string;
  totEwFeeAmt: number;
  /** 취소 접수일 (yyyy-MM-dd) — 환급액 산출 기준일 */
  cancelReqDate: string;
  totEwFeeRefundAmt: number;
  items: AsCareItem[];
}

export interface ClientCancelAsCareResponse
  extends ApiResponse<ClientAsCareCancelReceipt> {}

export const toClientAsCareCancelReceipt = (
  receipt: AsCareCancelReceipt,
): ClientAsCareCancelReceipt => ({
  ewPayGrpNo: receipt.ewPayGrpNo,
  orderNo: receipt.ordNo,
  store: receipt.saleShopNm,
  totEwFeeAmt: receipt.totEwFeeAmt,
  cancelReqDate: receipt.cancelReqDate,
  totEwFeeRefundAmt: receipt.totEwFeeRefundAmt,
  items: (receipt.orderLines ?? []).map((line) =>
    toCancelledItem(line, receipt.ewPayGrpNo, '', receipt.cancelReqDate),
  ),
});

/** 보험취소 접수 (SC-07) */
export class ClientCancelAsCareGateway {
  constructor(readonly httpClient: HttpClient) {}

  async exec(req: CancelAsCareRequest): Promise<ClientCancelAsCareResponse> {
    const apiResponse = await new CancelAsCareGateway(this.httpClient).exec(
      req,
    );

    if (apiResponse.code !== 200 || !apiResponse.data) {
      return { ...apiResponse, data: undefined };
    }

    return {
      ...apiResponse,
      data: toClientAsCareCancelReceipt(apiResponse.data),
    };
  }
}
